import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Edit } from "react-feather";
import DataTable from "../../../components/common/DataTable";
import { confirmDelete } from "../../../components/common/ConfirmDialog";
import { useMasterPermissions } from "../useMasterPermissions";
import {
  useGetDepartmentsQuery,
  useCreateDepartmentMutation,
  useUpdateDepartmentMutation,
  useDeleteDepartmentMutation,
} from "./departmentsApi";

// Mirrors GUI/Master/Frm_Department.cs, now against the live
// /api/v1/masters/departments endpoints (handoff §9.1). Global/shared
// master - reading requires Departments.View (route-gated, see AppRoutes.jsx),
// writes gated by Departments.Create/.Edit + an all-branch caller (§13.3).
const DepartmentsPage = () => {
  const { canCreate, canEdit } = useMasterPermissions("Departments");
  const { data: records = [], isLoading } = useGetDepartmentsQuery();
  const [createDepartment, { isLoading: isCreating }] = useCreateDepartmentMutation();
  const [updateDepartment, { isLoading: isUpdating }] = useUpdateDepartmentMutation();
  const [deleteDepartment] = useDeleteDepartmentMutation();

  const [mode, setMode] = useState("new"); // "new" | "view" | "edit"
  const [selectedCode, setSelectedCode] = useState(null);
  const [name, setName] = useState("");

  const selectedRecord = records.find((r) => r.departmentCode === selectedCode) || null;

  useEffect(() => {
    setName(mode === "new" ? "" : selectedRecord?.departmentName || "");
  }, [mode, selectedRecord]);

  const isReadOnly = mode === "view";
  const canWrite = mode === "new" ? canCreate : canEdit;

  const select = (code) => {
    setSelectedCode(code);
    setMode("view");
  };
  const startEdit = () => setMode("edit");
  const startNew = () => {
    setSelectedCode(null);
    setMode("new");
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    try {
      if (mode === "edit" && selectedCode != null) {
        await updateDepartment({ code: selectedCode, departmentName: name.trim() }).unwrap();
      } else {
        await createDepartment({ departmentName: name.trim() }).unwrap();
      }
      toast.success("Department saved");
      startNew();
    } catch (error) {
      toast.error(error?.message || "Could not save department");
    }
  };

  const handleDelete = async () => {
    if (!selectedRecord) return;
    const confirmed = await confirmDelete({ title: `Delete ${selectedRecord.departmentName}?` });
    if (!confirmed) return;
    try {
      await deleteDepartment(selectedRecord.departmentCode).unwrap();
      toast.success("Department deleted");
      startNew();
    } catch (error) {
      toast.error(error?.message || "Could not delete department");
    }
  };

  const columns = [
    { title: "Department", dataIndex: "departmentName" },
    {
      title: "",
      dataIndex: "actions",
      width: 60,
      render: (_, record) => (
        <button type="button" className="btn btn-link p-1" onClick={() => select(record.departmentCode)}>
          <Edit size={14} />
        </button>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Departments</h4>
              <h6>HR master data - department list</h6>
            </div>
          </div>
        </div>

        {!canCreate && !canEdit && (
          <div className="alert alert-info">
            You have read-only access. Managing departments requires Departments.Create/.Edit and
            an all-branch administrator.
          </div>
        )}

        <div className="card mb-4">
          <div className="card-body">
            <h5 className="mb-3">Details</h5>
            <div className="row">
              <div className="col-md-3 mb-3">
                <label className="form-label">Code</label>
                <input
                  type="text"
                  className="form-control"
                  value={mode === "new" ? "Auto-assigned" : selectedCode ?? ""}
                  readOnly
                  disabled
                />
              </div>
              <div className="col-md-7 mb-3">
                <label className="form-label">Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  readOnly={isReadOnly || !canWrite}
                  disabled={isReadOnly || !canWrite}
                />
              </div>
            </div>
            {(canCreate || canEdit) && (
              <div className="d-flex gap-2">
                {mode !== "view" && canWrite && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSave}
                    disabled={isCreating || isUpdating}
                  >
                    Save
                  </button>
                )}
                {mode === "view" && canEdit && (
                  <button type="button" className="btn btn-outline-primary" onClick={startEdit}>
                    Edit
                  </button>
                )}
                <button type="button" className="btn btn-secondary" onClick={startNew}>
                  Clear
                </button>
                {mode === "view" && canEdit && (
                  <button type="button" className="btn btn-outline-danger" onClick={handleDelete}>
                    Delete
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-responsive">
              <DataTable
                columns={columns}
                dataSource={records}
                rowKey="departmentCode"
                loading={isLoading}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DepartmentsPage;
